import { useState, useEffect, useRef } from "react";
import api, { BACKEND_URL } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBranding } from "../contexts/BrandingContext";
import { MapPin, Clock, Scissors, Save, Palette, Upload, X, RotateCcw, Check, Calendar, Shield, Plus, Trash2, Edit2, ChevronDown, Link2, Copy, ExternalLink, Tablet, Users, LayoutDashboard, ShoppingCart, Activity, DollarSign, Building2, RefreshCw, AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { BulkQualifyButton } from "../components/BulkQualifyDialog";

const PERIOD_PRESETS = [
  { value: 7,  label: "Weekly",        desc: "Paid every 7 days" },
  { value: 14, label: "Bi-Weekly",     desc: "Paid every 14 days (most common)" },
  { value: 15, label: "Semi-Monthly",  desc: "Paid ~twice per month (15 days)" },
  { value: 30, label: "Monthly",       desc: "Paid once per month (30 days)" },
];

function computeCurrentPeriod(lengthDays, anchorDate) {
  try {
    const anchor = new Date(anchorDate + "T00:00:00Z");
    const today = new Date();
    const daysSince = Math.max(0, Math.floor((today - anchor) / 86400000));
    const periodsElapsed = Math.floor(daysSince / lengthDays);
    const start = new Date(anchor.getTime() + periodsElapsed * lengthDays * 86400000);
    const end = new Date(start.getTime() + (lengthDays - 1) * 86400000);
    const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
    return { start, end, label: `${fmt(start)} — ${fmt(end)}` };
  } catch {
    return { label: "—" };
  }
}

function PayPeriodSection() {
  const [form, setForm] = useState({ length_days: 14, anchor_date: "2026-01-01" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/settings/pay-period").then(res => {
      setForm({
        length_days: res.data?.length_days || 14,
        anchor_date: res.data?.anchor_date || "2026-01-01",
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/pay-period", form);
      toast.success("Pay period saved — staff widgets will update next refresh");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const current = computeCurrentPeriod(form.length_days, form.anchor_date);
  const next = current.end
    ? computeCurrentPeriod(form.length_days, new Date(current.end.getTime() + 86400000).toISOString().slice(0, 10))
    : { label: "—" };

  if (loading) return null;

  return (
    <div className="space-y-6" data-testid="pay-period-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-['Outfit'] text-xl font-medium text-slate-900">Pay Period</h2>
          <p className="text-sm text-slate-400 mt-0.5">Controls the pay-period widget on every stylist's portal + payroll calculations</p>
        </div>
        <Button size="sm" onClick={save} disabled={saving} data-testid="save-pay-period-btn"
          className="h-9 bg-slate-900 text-white rounded-full px-5 text-xs hover:bg-slate-800">
          <Check className="w-3 h-3 mr-1.5" />{saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Preset cards */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3 font-['Manrope']">Pay Schedule</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="period-presets">
          {PERIOD_PRESETS.map(p => {
            const active = form.length_days === p.value;
            return (
              <button key={p.value}
                data-testid={`preset-${p.value}`}
                onClick={() => setForm(f => ({ ...f, length_days: p.value }))}
                className={`text-left rounded-2xl border p-4 transition-all ${active
                  ? "border-slate-900 bg-slate-900 text-white shadow-md -translate-y-0.5"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:-translate-y-0.5"}`}>
                <p className={`font-['Outfit'] font-semibold text-base ${active ? "text-white" : "text-slate-900"}`}>{p.label}</p>
                <p className={`text-[11px] mt-0.5 font-['Manrope'] ${active ? "text-white/70" : "text-slate-400"}`}>{p.desc}</p>
                <p className={`text-[9px] mt-2 tabular-nums font-bold uppercase tracking-widest ${active ? "text-white/60" : "text-slate-300"}`}>
                  {p.value} days
                </p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 block font-['Manrope']">Pay Anchor Date</label>
          <div className="flex items-center gap-3">
            <Input
              data-testid="pay-anchor-date"
              type="date"
              value={form.anchor_date}
              onChange={e => setForm(f => ({ ...f, anchor_date: e.target.value }))}
              className="w-48 h-10 border-slate-200 rounded-xl"
            />
            <p className="text-xs text-slate-500 font-['Manrope']">
              The first day of any past pay period. Future periods repeat every <strong>{form.length_days}</strong> days from this anchor.
            </p>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3 font-['Manrope']">Preview</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 rounded-xl p-4" data-testid="preview-current">
              <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-500 mb-1 font-['Manrope']">Current Period</p>
              <p className="font-['Outfit'] text-lg font-semibold text-slate-900 tabular-nums">{current.label}</p>
              <p className="text-[11px] text-slate-400 mt-1 font-['Manrope']">Staff see this on /my-portal right now</p>
            </div>
            <div className="bg-[#FAFAFA] border border-slate-200 rounded-xl p-4" data-testid="preview-next">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1 font-['Manrope']">Next Period</p>
              <p className="font-['Outfit'] text-lg font-semibold text-slate-600 tabular-nums">{next.label}</p>
              <p className="text-[11px] text-slate-400 mt-1 font-['Manrope']">Auto-starts when this one ends</p>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-start gap-2.5">
          <DollarSign className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-amber-900 font-['Manrope']">Heads up</p>
            <p className="text-[11px] text-amber-700 font-['Manrope'] mt-0.5">
              Changing the pay schedule doesn't retroactively re-calculate historical payroll.
              New periods will use these settings; past payroll reports remain on whatever cadence they were generated under.
            </p>
          </div>
        </div>
      </div>

      <SchedulingRulesCard />
    </div>
  );
}

function SchedulingRulesCard() {
  const [enforce, setEnforce] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/scheduling").then(r => {
      setEnforce(!!r.data?.enforce_working_hours);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    const next = !enforce;
    setSaving(true);
    try {
      await api.put("/settings/scheduling", { enforce_working_hours: next });
      setEnforce(next);
      toast.success(next ? "Enforcement ON — bookings locked to working hours" : "Enforcement OFF — bookings no longer blocked");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  if (loading) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="scheduling-rules-card">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3 font-['Manrope']">Booking Rules</p>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="font-['Outfit'] text-base font-semibold text-slate-900">Enforce working hours</p>
          <p className="text-[12px] text-slate-500 font-['Manrope'] mt-1 leading-relaxed">
            When on, appointments can't be booked or moved outside a stylist's scheduled shift at the selected location.
            Front-desk will get a clear red warning before saving.
          </p>
        </div>
        <button
          data-testid="enforce-working-hours-toggle"
          onClick={toggle}
          disabled={saving}
          className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors ${enforce ? "bg-slate-900" : "bg-slate-300"}`}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enforce ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
      <p className="text-[11px] text-slate-400 font-['Manrope'] mt-3 italic">
        Tip: this works hand-in-hand with the "Working today only" filter in the Calendar staff view.
      </p>
    </div>
  );
}
function OnboardingToursSection() {
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/onboarding-tour")
      .then(r => setEnabled(r.data?.enabled !== false))
      .catch(() => setEnabled(true))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    const next = !enabled;
    setSaving(true);
    try {
      await api.put("/settings/onboarding-tour", { enabled: next });
      setEnabled(next);
      toast.success(next
        ? "Onboarding tour enabled — the Help button is back on every page"
        : "Onboarding tour disabled — Help button hidden for everyone");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  if (loading) return null;

  return (
    <div className="space-y-6" data-testid="onboarding-tour-section">
      <div>
        <h2 className="font-['Outfit'] text-2xl font-light tracking-tight text-slate-900">Onboarding Tours</h2>
        <p className="text-slate-500 text-sm mt-1">
          Help new staff learn the platform with an interactive walkthrough that spotlights each module.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="onboarding-tour-card">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 font-['Manrope']">Hands-on tutorial</p>
            <p className="font-['Outfit'] text-base font-semibold text-slate-900">Show the Help / Tour button</p>
            <p className="text-[13px] text-slate-500 font-['Manrope'] mt-1.5 leading-relaxed">
              When on, every page shows a floating <span className="font-semibold text-slate-700">Help / Tour</span> button in the bottom-right. Tapping it starts a 12-step guided tour that highlights the real UI — Dashboard, Calendar, Clients, POS, Staff, Time Clock, Training, Payroll, Marketing, and Settings — with tooltips explaining what each one does.
            </p>
            <p className="text-[12px] text-slate-400 font-['Manrope'] mt-3 italic">
              Tip: leave this on while you're onboarding new hires; turn it off after the team is comfortable so the button doesn't clutter the screen.
            </p>
          </div>
          <button
            data-testid="onboarding-tour-toggle"
            onClick={toggle}
            disabled={saving}
            className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors ${enabled ? "bg-slate-900" : "bg-slate-300"}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
          <div className="rounded-xl border border-slate-100 bg-[#FAFAFA] p-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Steps</p>
            <p className="font-['Outfit'] text-2xl font-medium text-slate-900 mt-1">12</p>
            <p className="text-[11px] text-slate-500 font-['Manrope'] mt-0.5">Sequential walkthrough</p>
          </div>
          <div className="rounded-xl border border-slate-100 bg-[#FAFAFA] p-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Modules covered</p>
            <p className="font-['Outfit'] text-2xl font-medium text-slate-900 mt-1">10</p>
            <p className="text-[11px] text-slate-500 font-['Manrope'] mt-0.5">Across the whole app</p>
          </div>
          <div className="rounded-xl border border-slate-100 bg-[#FAFAFA] p-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Audience</p>
            <p className="font-['Outfit'] text-2xl font-medium text-slate-900 mt-1">Everyone</p>
            <p className="text-[11px] text-slate-500 font-['Manrope'] mt-0.5">Owners, managers, stylists</p>
          </div>
        </div>
      </div>
    </div>
  );
}



function LocationAdminCard({ loc, isAdmin, onChanged }) {
  const blank = () => ({ name: loc.name || "", address: loc.address || "", phone: loc.phone || "", email: loc.email || "", is_active: loc.is_active !== false });
  const [editOpen, setEditOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const openEdit = () => { setForm(blank()); setPin(""); setEditOpen(true); };
  const openRemove = () => { setPin(""); setRemoveOpen(true); };

  const saveEdit = async () => {
    if (pin.length !== 4) { toast.error("Enter your 4-digit Manager PIN"); return; }
    setBusy(true);
    try {
      await api.put(`/locations/${loc.location_id}`, { ...form, pin });
      toast.success("Location updated");
      setEditOpen(false); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update location"); }
    finally { setBusy(false); }
  };

  const confirmRemove = async () => {
    if (pin.length !== 4) { toast.error("Enter your 4-digit Manager PIN"); return; }
    setBusy(true);
    try {
      await api.delete(`/locations/${loc.location_id}`, { data: { pin } });
      toast.success("Location removed");
      setRemoveOpen(false); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove location"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid={`location-${loc.location_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-base font-medium text-slate-900">{loc.name}</h3>
          <p className="text-sm text-slate-600 mt-0.5">{loc.address}</p>
          <p className="text-sm text-slate-400 mt-0.5">{loc.phone}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs px-2 py-0.5 rounded-full ${loc.is_active ? 'bg-[#E8F0E9] text-emerald-600' : 'bg-[#FAFAFA] text-slate-400'}`}>
            {loc.is_active ? 'Active' : 'Inactive'}
          </span>
          {isAdmin && (
            <>
              <button onClick={openEdit} data-testid={`edit-location-${loc.location_id}`} title="Edit location"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={openRemove} data-testid={`remove-location-${loc.location_id}`} title="Remove location"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-rose-400 hover:text-rose-600 hover:bg-rose-50 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
      {loc.hours && typeof loc.hours === 'object' && (
        <div className="mt-4 grid grid-cols-7 gap-2">
          {Object.entries(loc.hours).map(([day, hrs]) => (
            <div key={day} className="text-center">
              <p className="text-[10px] font-bold uppercase text-slate-400">{day}</p>
              <p className="text-xs text-slate-600 mt-0.5">{hrs === 'closed' ? 'Closed' : hrs}</p>
            </div>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" data-testid="edit-location-dialog">
          <DialogHeader><DialogTitle>Edit location</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Name</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="edit-loc-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Address</label>
              <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} data-testid="edit-loc-address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500">Phone</label>
                <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="edit-loc-phone" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Email</label>
                <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="edit-loc-email" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-slate-900" data-testid="edit-loc-active" />
              Active (bookable)
            </label>
            <div className="pt-2 border-t border-slate-100">
              <label className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-1"><Shield className="w-3 h-3" /> Manager PIN (required)</label>
              <Input type="password" inputMode="numeric" maxLength={4} value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} placeholder="••••" data-testid="edit-loc-pin" className="tracking-[0.5em] text-center" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-full" data-testid="edit-loc-cancel">Cancel</Button>
              <Button onClick={saveEdit} disabled={busy} className="rounded-full bg-slate-900 text-white hover:bg-slate-800" data-testid="edit-loc-save">{busy ? "Saving…" : "Save changes"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove dialog */}
      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent className="max-w-md" data-testid="remove-location-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-rose-600"><AlertTriangle className="w-5 h-5" /> Remove location</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">This permanently removes <b>{loc.name}</b>. This can't be undone. Enter your Manager PIN to confirm.</p>
            <div>
              <label className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-1"><Shield className="w-3 h-3" /> Manager PIN</label>
              <Input type="password" inputMode="numeric" maxLength={4} value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} placeholder="••••" data-testid="remove-loc-pin" className="tracking-[0.5em] text-center" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setRemoveOpen(false)} className="rounded-full" data-testid="remove-loc-cancel">Cancel</Button>
              <Button onClick={confirmRemove} disabled={busy} className="rounded-full bg-rose-600 text-white hover:bg-rose-700" data-testid="remove-loc-confirm">{busy ? "Removing…" : "Remove location"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("workspace");
  const [tenantInfo, setTenantInfo] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const branding = useBranding();
  const [brandForm, setBrandForm] = useState({
    app_name: "", logo_url: "", primary_color: "#1A1A1A", accent_color: "#B5A493",
    sidebar_bg: "#FFFFFF", sidebar_text: "#5E5E5E", sidebar_active_bg: "#1A1A1A", sidebar_active_text: "#FFFFFF",
  });
  const [brandSaving, setBrandSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoRef = useRef(null);
  const [apptSettings, setApptSettings] = useState({ noshow_timeout_minutes: 15 });
  const [apptSaving, setApptSaving] = useState(false);
  const [accessLevels, setAccessLevels] = useState([]);
  const [modules, setModules] = useState([]);
  const [editingLevel, setEditingLevel] = useState(null);
  const [newLevelName, setNewLevelName] = useState("");
  const [newLevelDesc, setNewLevelDesc] = useState("");
  const [showNewLevel, setShowNewLevel] = useState(false);
  const [savingLevel, setSavingLevel] = useState(false);

  // Load branding form on mount
  useEffect(() => {
    setBrandForm({
      app_name: branding.app_name || "SalonFlow",
      logo_url: branding.logo_url || "",
      primary_color: branding.primary_color || "#1A1A1A",
      accent_color: branding.accent_color || "#B5A493",
      sidebar_bg: branding.sidebar_bg || "#FFFFFF",
      sidebar_text: branding.sidebar_text || "#5E5E5E",
      sidebar_active_bg: branding.sidebar_active_bg || "#1A1A1A",
      sidebar_active_text: branding.sidebar_active_text || "#FFFFFF",
    });
  }, [branding]);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/settings/upload-logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setBrandForm(f => ({ ...f, logo_url: res.data.url }));
      toast.success("Logo uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setLogoUploading(false); }
  };

  const saveBranding = async () => {
    setBrandSaving(true);
    try {
      const ok = await branding.updateBranding(brandForm);
      if (ok) toast.success("Branding saved — changes applied");
      else toast.error("Failed to save");
    } catch { toast.error("Failed to save"); }
    finally { setBrandSaving(false); }
  };

  const resetBranding = () => {
    setBrandForm({
      app_name: "SalonFlow", logo_url: "", primary_color: "#1A1A1A", accent_color: "#B5A493",
      sidebar_bg: "#FFFFFF", sidebar_text: "#5E5E5E", sidebar_active_bg: "#1A1A1A", sidebar_active_text: "#FFFFFF",
    });
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [locRes, svcRes, apptRes, lvlRes, modRes, tenantRes] = await Promise.all([
          api.get("/locations"),
          api.get("/services"),
          api.get("/settings/appointments"),
          api.get("/access-levels"),
          api.get("/access-levels/modules"),
          api.get("/tenants/me").catch(() => ({ data: null })),
        ]);
        setLocations(locRes.data);
        setServices(svcRes.data);
        setApptSettings(apptRes.data || { noshow_timeout_minutes: 15 });
        setAccessLevels(lvlRes.data || []);
        setModules(modRes.data || []);
        setTenantInfo(tenantRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const copyTenantId = async () => {
    const tid = tenantInfo?.tenant_id || user?.tenant_id;
    if (!tid) return;
    try {
      await navigator.clipboard.writeText(tid);
      toast.success("Organization ID copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const runHealthCheck = async () => {
    setHealthLoading(true);
    try {
      const res = await api.get("/admin/tenant-health");
      setHealth(res.data);
      if (res.data.total_untagged === 0) toast.success("Workspace is healthy");
      else toast.warning(`${res.data.total_untagged} untagged documents found`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Health check failed");
    } finally {
      setHealthLoading(false);
    }
  };

  const runMigration = async () => {
    if (!window.confirm("Re-run the tenant-id migration? This is safe and idempotent.")) return;
    setMigrating(true);
    try {
      const res = await api.post("/admin/run-migration");
      toast.success(`Migration complete — ${res.data.total_modified} documents updated`);
      await runHealthCheck();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  const isAdmin = user?.role === "owner" || user?.role === "manager";
  const isOwner = user?.role === "owner";
  const refetchLocations = async () => {
    try { const r = await api.get("/locations"); setLocations(r.data); } catch { /* silent */ }
  };
  const sections = [
    { id: "workspace", label: "Workspace", icon: Building2 },
    { id: "branding", label: "Branding", icon: Palette },
    { id: "appointments", label: "Appointments", icon: Calendar },
    ...(isAdmin ? [{ id: "pay-period", label: "Pay Period", icon: DollarSign }] : []),
    ...(isAdmin ? [{ id: "onboarding", label: "Onboarding Tours", icon: Sparkles }] : []),
    { id: "access", label: "Access Levels", icon: Shield },
    { id: "locations", label: "Locations", icon: MapPin },
    { id: "services", label: "Services", icon: Scissors },
    { id: "hours", label: "Business Hours", icon: Clock },
    { id: "links", label: "Portal Links", icon: Link2 },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="settings-page">
      <div className="mb-8 animate-fadeIn">
        <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Settings</h1>
        <p className="text-slate-600 text-sm mt-1">Manage your salon configuration</p>
      </div>

      <div className="flex gap-8">
        {/* Settings Nav */}
        <div className="w-[200px] flex-shrink-0">
          <nav className="space-y-1">
            {sections.map(s => (
              <button
                key={s.id}
                data-testid={`settings-nav-${s.id}`}
                onClick={() => setActiveSection(s.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeSection === s.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-[#FAFAFA]"
                }`}
              >
                <s.icon className="w-4 h-4" strokeWidth={1.5} />
                {s.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Settings Content */}
        <div className="flex-1">
          {activeSection === "workspace" && (
            <div className="space-y-6" data-testid="workspace-section">
              <div>
                <h2 className="font-['Outfit'] text-2xl font-light tracking-tight text-slate-900">Workspace</h2>
                <p className="text-slate-500 text-sm mt-1">Your organization's unique identifier and health diagnostics</p>
              </div>

              {/* Organization Identity Card */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-orange-400 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-white" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Organization</p>
                    <h3 className="font-['Outfit'] text-xl font-semibold text-slate-900 mt-1" data-testid="workspace-business-name">
                      {tenantInfo?.business_name || "—"}
                    </h3>
                    {tenantInfo?.slug && (
                      <p className="text-sm text-slate-500 mt-0.5">/t/{tenantInfo.slug}</p>
                    )}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-slate-100 space-y-4">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Organization ID</label>
                    <p className="text-[11px] text-slate-500 mt-1">Share this with support to quickly identify your workspace</p>
                    <div className="flex items-center gap-2 mt-2">
                      <code
                        data-testid="tenant-id-display"
                        className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg font-mono text-sm text-slate-900 select-all break-all"
                      >
                        {tenantInfo?.tenant_id || user?.tenant_id || "—"}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyTenantId}
                        data-testid="copy-tenant-id-btn"
                        className="flex-shrink-0 h-[42px]"
                      >
                        <Copy className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
                        Copy
                      </Button>
                    </div>
                  </div>

                  {tenantInfo?.plan_status && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Plan</span>
                      <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[11px] font-semibold uppercase">
                        {tenantInfo.plan_status}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Workspace Health (Owner only) */}
              {isOwner && (
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="workspace-health-card">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-['Outfit'] text-lg font-semibold text-slate-900">Workspace Health</h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        Scans for documents missing a tenant ID. Run after deploys if data looks missing.
                      </p>
                    </div>
                    <Button
                      onClick={runHealthCheck}
                      disabled={healthLoading}
                      data-testid="run-health-check-btn"
                      className="flex-shrink-0"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${healthLoading ? "animate-spin" : ""}`} strokeWidth={1.75} />
                      {healthLoading ? "Checking…" : "Run Health Check"}
                    </Button>
                  </div>

                  {health && (
                    <div className="mt-5 space-y-4">
                      {health.total_untagged === 0 ? (
                        <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-100 rounded-xl" data-testid="health-status-healthy">
                          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                          <div>
                            <p className="font-semibold text-emerald-900 text-sm">All data is correctly tagged</p>
                            <p className="text-xs text-emerald-700 mt-0.5">Every document has a tenant ID. Your workspace is healthy.</p>
                          </div>
                        </div>
                      ) : (
                        <div data-testid="health-status-warning">
                          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                            <div className="flex-1">
                              <p className="font-semibold text-amber-900 text-sm">{health.total_untagged} untagged documents found</p>
                              <p className="text-xs text-amber-700 mt-0.5">These documents are invisible until they are stamped with a tenant ID. Click Re-run Migration below to fix.</p>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            {Object.entries(health.untagged_by_collection).map(([coll, cnt]) => (
                              <div key={coll} className="flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg">
                                <span className="text-xs font-mono text-slate-700">{coll}</span>
                                <span className="text-xs font-bold text-amber-700 tabular-nums">{cnt}</span>
                              </div>
                            ))}
                          </div>
                          <Button
                            onClick={runMigration}
                            disabled={migrating}
                            variant="outline"
                            className="mt-3"
                            data-testid="run-migration-btn"
                          >
                            <RefreshCw className={`w-4 h-4 mr-2 ${migrating ? "animate-spin" : ""}`} strokeWidth={1.75} />
                            {migrating ? "Running…" : "Re-run Migration"}
                          </Button>
                        </div>
                      )}

                      {/* My tenant counts for reference */}
                      {Object.keys(health.my_tenant_counts || {}).length > 0 && (
                        <div className="pt-4 border-t border-slate-100">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Your Workspace Data</p>
                          <div className="grid grid-cols-3 gap-2">
                            {Object.entries(health.my_tenant_counts).map(([coll, cnt]) => (
                              <div key={coll} className="px-3 py-2 bg-slate-50 rounded-lg">
                                <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{coll}</p>
                                <p className="text-base font-semibold text-slate-900 tabular-nums">{cnt}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeSection === "branding" && (
            <div className="space-y-6" data-testid="branding-section">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-['Outfit'] text-xl font-medium text-slate-900">Branding</h2>
                  <p className="text-sm text-slate-400 mt-0.5">Customize your salon's logo, name, and colors</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={resetBranding} data-testid="reset-branding-btn"
                    className="h-9 border-slate-200 rounded-full px-4 text-xs">
                    <RotateCcw className="w-3 h-3 mr-1.5" />Reset
                  </Button>
                  <Button size="sm" onClick={saveBranding} disabled={brandSaving} data-testid="save-branding-btn"
                    className="h-9 bg-slate-900 text-white rounded-full px-5 text-xs hover:bg-slate-800">
                    <Check className="w-3 h-3 mr-1.5" />{brandSaving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>

              {/* Logo */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Logo</h3>
                <div className="flex items-center gap-6">
                  <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={e => handleLogoUpload(e.target.files[0])} />
                  <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden bg-[#FAFAFA]">
                    {brandForm.logo_url ? (
                      <img src={brandForm.logo_url.startsWith("http") ? brandForm.logo_url : `${BACKEND_URL}${brandForm.logo_url}`} alt="Logo" className="w-full h-full object-cover rounded-2xl" data-testid="branding-logo-preview" />
                    ) : (
                      <Scissors className="w-8 h-8 text-[#E6E4E0]" />
                    )}
                  </div>
                  <div>
                    <Button variant="outline" onClick={() => logoRef.current?.click()} disabled={logoUploading} data-testid="upload-logo-btn"
                      className="border-slate-200 rounded-full px-4 h-9 text-xs">
                      <Upload className="w-3 h-3 mr-1.5" />{logoUploading ? "Uploading..." : "Upload Logo"}
                    </Button>
                    {brandForm.logo_url && (
                      <button onClick={() => setBrandForm(f => ({ ...f, logo_url: "" }))} className="ml-2 text-xs text-slate-400 hover:text-rose-600" data-testid="remove-logo-btn">
                        Remove
                      </button>
                    )}
                    <p className="text-xs text-slate-400 mt-2">PNG, JPG, SVG or WebP. Max 5MB.</p>
                  </div>
                </div>
              </div>

              {/* App Name */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">App Name</h3>
                <Input data-testid="branding-app-name" value={brandForm.app_name} onChange={e => setBrandForm(f => ({ ...f, app_name: e.target.value }))}
                  placeholder="Your salon name" className="border-slate-200 max-w-sm" />
                <p className="text-xs text-slate-400 mt-2">Displayed in the sidebar and browser tab</p>
              </div>

              {/* Colors */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Colors</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { key: "primary_color", label: "Primary Color", desc: "Logo circle, buttons, headings" },
                    { key: "accent_color", label: "Accent Color", desc: "Secondary elements, borders" },
                    { key: "sidebar_bg", label: "Sidebar Background", desc: "Navigation panel background" },
                    { key: "sidebar_text", label: "Sidebar Text", desc: "Navigation link color" },
                    { key: "sidebar_active_bg", label: "Active Nav Background", desc: "Selected menu item background" },
                    { key: "sidebar_active_text", label: "Active Nav Text", desc: "Selected menu item text" },
                  ].map(c => (
                    <div key={c.key} className="flex items-center gap-4" data-testid={`color-${c.key}`}>
                      <div className="relative">
                        <input
                          type="color"
                          value={brandForm[c.key]}
                          onChange={e => setBrandForm(f => ({ ...f, [c.key]: e.target.value }))}
                          className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200 p-0.5"
                          data-testid={`color-picker-${c.key}`}
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{c.label}</p>
                        <p className="text-xs text-slate-400">{c.desc}</p>
                        <Input
                          value={brandForm[c.key]}
                          onChange={e => setBrandForm(f => ({ ...f, [c.key]: e.target.value }))}
                          className="mt-1 h-7 text-xs border-slate-200 w-28 font-mono"
                          data-testid={`color-hex-${c.key}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Live Preview */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Preview</h3>
                <div className="flex gap-4 items-stretch">
                  <div className="w-48 rounded-2xl border border-slate-200 overflow-hidden" style={{ backgroundColor: brandForm.sidebar_bg }}>
                    <div className="px-3 py-3 border-b border-slate-200/30 flex items-center gap-2">
                      {brandForm.logo_url ? (
                        <img src={brandForm.logo_url.startsWith("http") ? brandForm.logo_url : `${BACKEND_URL}${brandForm.logo_url}`} alt="" className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: brandForm.primary_color }}>
                          <Scissors className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <span className="text-sm font-medium" style={{ color: brandForm.primary_color }}>{brandForm.app_name || "SalonFlow"}</span>
                    </div>
                    <div className="p-2 space-y-0.5">
                      <div className="px-2.5 py-2 rounded-md text-xs font-medium" style={{ backgroundColor: brandForm.sidebar_active_bg, color: brandForm.sidebar_active_text }}>
                        Dashboard
                      </div>
                      {["Calendar", "Clients", "Checkout"].map(n => (
                        <div key={n} className="px-2.5 py-2 rounded-md text-xs" style={{ color: brandForm.sidebar_text }}>
                          {n}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1 rounded-2xl border border-slate-200 p-4 bg-[#FAFAFA]">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: brandForm.primary_color }} />
                      <span className="text-sm font-medium" style={{ color: brandForm.primary_color }}>{brandForm.app_name}</span>
                    </div>
                    <div className="flex gap-2">
                      <div className="px-3 py-1.5 rounded-full text-xs text-white" style={{ backgroundColor: brandForm.primary_color }}>Primary Button</div>
                      <div className="px-3 py-1.5 rounded-full text-xs border" style={{ borderColor: brandForm.accent_color, color: brandForm.accent_color }}>Accent Button</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === "appointments" && (
            <div className="space-y-6" data-testid="appointments-settings-section">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-['Outfit'] text-xl font-medium text-slate-900">Appointment Settings</h2>
                  <p className="text-sm text-slate-400 mt-0.5">Configure appointment status rules and automation</p>
                </div>
                <Button size="sm" disabled={apptSaving} data-testid="save-appt-settings-btn"
                  onClick={async () => {
                    setApptSaving(true);
                    try {
                      await api.put("/settings/appointments", apptSettings);
                      toast.success("Appointment settings saved");
                    } catch { toast.error("Failed to save"); }
                    setApptSaving(false);
                  }}
                  className="h-9 bg-slate-900 text-white rounded-full px-5 text-xs hover:bg-slate-800">
                  <Check className="w-3 h-3 mr-1.5" />{apptSaving ? "Saving..." : "Save"}
                </Button>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">No-Show Auto-Detection</h3>
                  <p className="text-xs text-slate-400 mb-4">
                    If a client hasn't checked in by their appointment time, the status will automatically change to "Late". After the timeout period below, it will switch to "No-Show".
                  </p>
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-slate-600 whitespace-nowrap">No-show timeout</label>
                    <Input
                      data-testid="noshow-timeout-input"
                      type="number"
                      min={1}
                      max={120}
                      value={apptSettings.noshow_timeout_minutes}
                      onChange={e => setApptSettings(s => ({ ...s, noshow_timeout_minutes: parseInt(e.target.value) || 15 }))}
                      className="w-24 h-10 border-slate-200 rounded-xl text-center font-semibold"
                    />
                    <span className="text-sm text-slate-500">minutes after appointment start time</span>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-5">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">Status Flow</h3>
                  <div className="flex items-center gap-3 flex-wrap">
                    {[
                      { label: "Booked", color: "#3B82F6", desc: "Initial booking" },
                      { label: "Late", color: "#F97316", desc: "Past start time, not checked in" },
                      { label: "In Service", color: "#22C55E", desc: "Client checked in, service started" },
                      { label: "Completed", color: "#16A34A", desc: "Service finished" },
                      { label: "No-Show", color: "#EF4444", desc: "Auto-set after timeout" },
                    ].map((s, i) => (
                      <div key={s.label} className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                          <span className="text-xs font-semibold text-slate-700">{s.label}</span>
                        </div>
                        {i < 4 && <span className="text-slate-300 text-lg">→</span>}
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-3">
                    Booked → automatically becomes Late when past appointment time → becomes No-Show after {apptSettings.noshow_timeout_minutes} minutes. Staff can manually set "In Service" or "Completed" at any time.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeSection === "pay-period" && <PayPeriodSection />}

          {activeSection === "onboarding" && <OnboardingToursSection />}

          {activeSection === "access" && (
            <div className="space-y-6" data-testid="access-levels-section">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-['Outfit'] text-xl font-medium text-slate-900">Access Levels</h2>
                  <p className="text-sm text-slate-400 mt-0.5">Define what each role can see and do across the system</p>
                </div>
                <Button size="sm" onClick={() => setShowNewLevel(true)} data-testid="create-level-btn"
                  className="h-9 gradient-primary text-white rounded-full px-5 text-xs font-semibold shadow-sm">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> New Level
                </Button>
              </div>

              {/* Create New Level */}
              {showNewLevel && (
                <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="new-level-form">
                  <h3 className="text-sm font-semibold text-slate-900">Create Access Level</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Name</label>
                      <Input value={newLevelName} onChange={e => setNewLevelName(e.target.value)} placeholder="e.g., Senior Stylist" className="h-9 border-slate-200 rounded-lg text-sm" data-testid="new-level-name" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Description</label>
                      <Input value={newLevelDesc} onChange={e => setNewLevelDesc(e.target.value)} placeholder="Brief description" className="h-9 border-slate-200 rounded-lg text-sm" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" disabled={savingLevel || !newLevelName.trim()} data-testid="save-new-level-btn"
                      onClick={async () => {
                        setSavingLevel(true);
                        try {
                          const res = await api.post("/access-levels", { name: newLevelName, description: newLevelDesc });
                          setAccessLevels(prev => [...prev, res.data]);
                          setShowNewLevel(false); setNewLevelName(""); setNewLevelDesc("");
                          toast.success("Access level created");
                        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                        setSavingLevel(false);
                      }}
                      className="h-8 bg-slate-900 text-white rounded-lg px-4 text-xs">
                      <Check className="w-3 h-3 mr-1" /> Create
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowNewLevel(false)} className="h-8 text-xs text-slate-500">Cancel</Button>
                  </div>
                </div>
              )}

              {/* Access Level Cards */}
              {accessLevels.map(level => {
                const isEditing = editingLevel === level.level_id;
                const perms = level.permissions || {};
                const viewCount = modules.filter(m => perms[m]?.view).length;
                const editCount = modules.filter(m => perms[m]?.edit).length;

                return (
                  <div key={level.level_id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`level-${level.level_id}`}>
                    {/* Level Header */}
                    <div className="p-5 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${level.is_system ? 'bg-violet-50' : 'bg-rose-50'}`}>
                          <Shield className={`w-4 h-4 ${level.is_system ? 'text-violet-500' : 'text-rose-500'}`} strokeWidth={1.5} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-slate-900 font-['Outfit']">{level.name}</h3>
                            {level.is_system && <span className="text-[9px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 font-bold uppercase tracking-wider">System</span>}
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5 font-['Manrope']">{level.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right text-xs font-['Manrope']">
                          <p className="text-slate-500">{viewCount}/{modules.length} modules visible</p>
                          <p className="text-slate-400">{editCount} editable</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setEditingLevel(isEditing ? null : level.level_id)} data-testid={`edit-level-${level.level_id}`}
                          className="h-8 px-2 text-slate-400 hover:text-rose-500">
                          {isEditing ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                        </Button>
                        {!level.is_system && (
                          <Button variant="ghost" size="sm" data-testid={`delete-level-${level.level_id}`}
                            onClick={async () => {
                              try { await api.delete(`/access-levels/${level.level_id}`); setAccessLevels(prev => prev.filter(l => l.level_id !== level.level_id)); toast.success("Deleted"); }
                              catch { toast.error("Failed"); }
                            }}
                            className="h-8 px-2 text-slate-300 hover:text-rose-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Permission Grid (Expanded) */}
                    {isEditing && (
                      <div className="border-t border-slate-100 p-5 bg-slate-50/50">
                        <div className="mb-3 flex items-center justify-between">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Module Permissions</p>
                          <Button size="sm" data-testid={`save-perms-${level.level_id}`}
                            onClick={async () => {
                              try {
                                await api.put(`/access-levels/${level.level_id}`, { permissions: perms });
                                toast.success("Permissions saved");
                                setEditingLevel(null);
                              } catch { toast.error("Failed"); }
                            }}
                            className="h-7 bg-slate-900 text-white rounded-lg px-3 text-[10px] font-semibold">
                            <Check className="w-3 h-3 mr-1" /> Save Permissions
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 gap-0">
                          {/* Header */}
                          <div className="grid grid-cols-[1fr,60px,60px,60px,60px] gap-1 py-1.5 px-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                            <span>Module</span><span className="text-center">View</span><span className="text-center">Create</span><span className="text-center">Edit</span><span className="text-center">Delete</span>
                          </div>
                          {modules.map(mod => {
                            const mp = perms[mod] || { view: false, create: false, edit: false, delete: false };
                            const togglePerm = (perm) => {
                              const updated = { ...perms, [mod]: { ...mp, [perm]: !mp[perm] } };
                              if (perm === "view" && mp.view) { updated[mod] = { view: false, create: false, edit: false, delete: false }; }
                              if (perm !== "view" && !mp.view) { updated[mod].view = true; }
                              setAccessLevels(prev => prev.map(l => l.level_id === level.level_id ? { ...l, permissions: updated } : l));
                            };
                            return (
                              <div key={mod} className="grid grid-cols-[1fr,60px,60px,60px,60px] gap-1 py-1.5 px-2 hover:bg-white rounded-lg transition-colors items-center">
                                <span className="text-xs text-slate-700 font-medium capitalize font-['Manrope']">{mod.replace(/_/g, " ")}</span>
                                {["view", "create", "edit", "delete"].map(perm => (
                                  <div key={perm} className="flex justify-center">
                                    <button onClick={() => togglePerm(perm)} data-testid={`perm-${level.level_id}-${mod}-${perm}`}
                                      className={`w-6 h-6 rounded-md flex items-center justify-center transition-all ${
                                        mp[perm] ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-300'
                                      }`}>
                                      {mp[perm] ? <Check className="w-3 h-3" strokeWidth={2.5} /> : <X className="w-3 h-3" strokeWidth={1.5} />}
                                    </button>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {activeSection === "locations" && (
            <div className="space-y-4">
              <h2 className="font-['Outfit'] text-xl font-medium text-slate-900">Locations</h2>
              {locations.map(loc => (
                <LocationAdminCard key={loc.location_id} loc={loc} isAdmin={isAdmin} onChanged={refetchLocations} />
              ))}
            </div>
          )}

          {activeSection === "services" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-['Outfit'] text-xl font-medium text-slate-900">Service Menu</h2>
                {isAdmin && (
                  <p className="text-xs text-slate-500 font-['Manrope']">
                    Tip: click <span className="font-semibold text-slate-700">Bulk Qualify</span> on any service to mark every active stylist who can perform it.
                  </p>
                )}
              </div>
              {/* Group by category */}
              {Object.entries(
                services.reduce((acc, s) => {
                  (acc[s.category] = acc[s.category] || []).push(s);
                  return acc;
                }, {})
              ).map(([category, svcs]) => (
                <div key={category}>
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">{category}</h3>
                  <div className="space-y-2">
                    {svcs.map(svc => (
                      <div key={svc.service_id} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between" data-testid={`service-${svc.service_id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: svc.color }} />
                          <div>
                            <p className="text-sm font-medium text-slate-900">{svc.name}</p>
                            <p className="text-xs text-slate-400">{svc.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-slate-400">{svc.duration}min</span>
                          <span className="font-medium text-slate-900">${svc.price}</span>
                          {isAdmin && <BulkQualifyButton service={svc} />}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeSection === "hours" && (
            <div className="space-y-4">
              <h2 className="font-['Outfit'] text-xl font-medium text-slate-900">Business Hours</h2>
              <p className="text-sm text-slate-600">Business hours are configured per location. Select a location above to edit hours.</p>
              {locations.map(loc => (
                <div key={loc.location_id} className="bg-white border border-slate-200 rounded-2xl p-6">
                  <h3 className="text-base font-medium text-slate-900 mb-4">{loc.name}</h3>
                  <div className="space-y-2">
                    {loc.hours && Object.entries(loc.hours).map(([day, hrs]) => (
                      <div key={day} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                        <span className="text-sm font-medium text-slate-900 capitalize w-24">{day}</span>
                        <span className="text-sm text-slate-600">{hrs === 'closed' ? 'Closed' : hrs}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeSection === "links" && <PortalLinksSection />}
        </div>
      </div>
    </div>
  );
}

// ───────────────────── Portal Links Section ─────────────────────
function PortalLinksSection() {
  const [copiedKey, setCopiedKey] = useState(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const handleCopy = (key, path) => {
    const full = `${origin}${path}`;
    const onOk = () => {
      setCopiedKey(key);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopiedKey(null), 2000);
    };
    const onFail = () => {
      // Fallback: create a hidden textarea + execCommand (works in restricted contexts)
      try {
        const ta = document.createElement("textarea");
        ta.value = full;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        onOk();
      } catch {
        toast.error("Couldn't copy — select and copy manually");
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(full).then(onOk).catch(onFail);
    } else {
      onFail();
    }
  };

  const groups = [
    {
      title: "Client-Facing",
      desc: "Share these with your clients. Great for social bios, email signatures, and QR codes.",
      links: [
        { key: "home", label: "Salon Home", path: "/salon", description: "Main public site with services, team, and booking", icon: LayoutDashboard, accent: "rose" },
        { key: "book", label: "Online Booking", path: "/salon/book", description: "Direct link to the booking flow (requires card-on-file for new clients)", icon: Calendar, accent: "violet" },
        { key: "services", label: "Services Menu", path: "/salon/services", description: "Full service catalog with prices and durations", icon: Scissors, accent: "teal" },
        { key: "team", label: "Meet the Team", path: "/salon/team", description: "Staff directory with book-with-stylist CTAs", icon: Users, accent: "amber" },
      ],
    },
    {
      title: "Staff & Kiosks",
      desc: "URLs for in-salon devices — tablets, staff phones, and self-service stations.",
      links: [
        { key: "clock", label: "Time Clock Kiosk", path: "/clock", description: "Standing tablet for staff to clock in/out with a PIN. Requires pairing code.", icon: Tablet, accent: "violet" },
        { key: "employee-login", label: "Employee Login", path: "/employee/login", description: "PIN quick-login grid for staff mobile access", icon: Shield, accent: "rose" },
        { key: "employee", label: "Employee Portal", path: "/employee", description: "Staff dashboard — appointments, clients, clock status", icon: LayoutDashboard, accent: "teal" },
      ],
    },
    {
      title: "Manager System",
      desc: "Admin-only dashboard and operations pages.",
      links: [
        { key: "login", label: "Manager Login", path: "/login", description: "Owner, manager, and admin sign-in", icon: Shield, accent: "slate" },
        { key: "dashboard", label: "Main Dashboard", path: "/dashboard", description: "Owner/manager overview — KPIs, quick actions, pipeline", icon: LayoutDashboard, accent: "rose" },
        { key: "calendar", label: "Calendar", path: "/calendar", description: "Drag-and-drop appointment scheduling", icon: Calendar, accent: "violet" },
        { key: "pos", label: "Checkout (POS)", path: "/pos", description: "Point-of-sale for in-salon transactions", icon: ShoppingCart, accent: "teal" },
        { key: "location-pulse", label: "Location Pulse", path: "/location-pulse", description: "Real-time multi-location ops board", icon: Activity, accent: "amber" },
      ],
    },
  ];

  const accentMap = {
    rose:   { bg: "bg-rose-50",   icon: "text-rose-500",   border: "hover:border-rose-200" },
    violet: { bg: "bg-violet-50", icon: "text-violet-500", border: "hover:border-violet-200" },
    teal:   { bg: "bg-teal-50",   icon: "text-teal-600",   border: "hover:border-teal-200" },
    amber:  { bg: "bg-amber-50",  icon: "text-amber-600",  border: "hover:border-amber-200" },
    slate:  { bg: "bg-slate-100", icon: "text-slate-700",  border: "hover:border-slate-300" },
  };

  return (
    <div className="space-y-8" data-testid="portal-links-section">
      <div>
        <h2 className="font-['Outfit'] text-xl font-medium text-slate-900">Portal Links</h2>
        <p className="text-sm text-slate-400 mt-0.5">Quick-share URLs for every portal and kiosk. Click to open, or copy to share.</p>
      </div>

      <div className="bg-gradient-to-r from-slate-50 to-white border border-slate-100 rounded-2xl p-4 flex items-center gap-3" data-testid="portal-links-origin">
        <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
          <Link2 className="w-4 h-4 text-slate-500" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Your base URL</p>
          <p className="text-sm font-mono text-slate-900 truncate">{origin}</p>
        </div>
        <button onClick={() => handleCopy("origin", "")}
          data-testid="copy-origin"
          className="flex-shrink-0 h-9 px-3 rounded-lg border border-slate-200 hover:border-slate-300 text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          {copiedKey === "origin" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
          {copiedKey === "origin" ? "Copied" : "Copy"}
        </button>
      </div>

      {groups.map(g => (
        <div key={g.title}>
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-slate-900 font-['Outfit']">{g.title}</h3>
            <p className="text-xs text-slate-400 font-['Manrope'] mt-0.5">{g.desc}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {g.links.map(l => {
              const a = accentMap[l.accent] || accentMap.slate;
              return (
                <div key={l.key}
                  data-testid={`portal-link-${l.key}`}
                  className={`group bg-white border border-slate-100 rounded-2xl p-4 transition-all ${a.border}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl ${a.bg} flex items-center justify-center flex-shrink-0`}>
                      <l.icon className={`w-4 h-4 ${a.icon}`} strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <p className="text-sm font-semibold text-slate-900 font-['Outfit']">{l.label}</p>
                        <code className="text-[11px] text-slate-400 font-mono truncate">{l.path}</code>
                      </div>
                      <p className="text-xs text-slate-500 font-['Manrope'] leading-relaxed">{l.description}</p>
                      <div className="flex items-center gap-1.5 mt-3">
                        <a href={l.path} target="_blank" rel="noreferrer"
                          data-testid={`open-${l.key}`}
                          className="h-8 px-3 rounded-lg bg-slate-900 text-white text-[11px] font-semibold flex items-center gap-1 hover:bg-slate-800">
                          <ExternalLink className="w-3 h-3" />Open
                        </a>
                        <button onClick={() => handleCopy(l.key, l.path)}
                          data-testid={`copy-${l.key}`}
                          className="h-8 px-3 rounded-lg border border-slate-200 hover:border-slate-300 text-[11px] font-semibold text-slate-700 flex items-center gap-1">
                          {copiedKey === l.key ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                          {copiedKey === l.key ? "Copied" : "Copy"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
